import { ActionTypes } from "../constants/action-types";
import { contentListToContentByDate } from "../ai-content/types/AIContentTypes";
import { fromJS, List, Map } from "immutable";
import { handleActions } from "redux-actions";
import { getDefaultTimezone } from "../ai-content/constants/timezoneConstants";
import { formatInTimeZone } from "date-fns-tz";
import { parseISO, isValid } from "date-fns";

const initialState = fromJS({
  generatedContent: "",
  error: null,
  settings: {},
  socialPosts: [],
  apiMetadata: null,
  platformFilter: "All", // Default to show all platforms
  statusFilter: "All",
  timezoneFilter: "All", // Default to show all timezones
  timezone: getDefaultTimezone(),
  currentDate: new Date(),

  /** When true, next generate must use override=true so new AI settings apply. Cleared when generate runs. */
  settingsChangedSinceLastGenerate: false,

  canvaDesigns: [],
  fileImages: [],
  showPostDetail: false,
  postDetail: null,

  showSharePost: false,
  sharePost: null,

  // TopicsModal state
  isTopicsModalOpen: false,
  selectedTopicIds: [],
  selectedTopic: null,
});

export const actions = {
  [ActionTypes.searchSocialPosts]: {
    next(state: Map<string, any>, action: any) {
      const { payload, error } = action;
      if (error || payload == null) {
        return state.merge({ error });
      }

      const data = payload?.data || payload;
      const pageResult = data?.pageResult || {};
      const posts: any[] = Array.isArray(pageResult?.content) ? pageResult.content : [];

      return state.merge({
        socialPosts: fromJS(posts),
        apiMetadata: data?.metadata || null,
        error: null,
      });
    },
  },

  [ActionTypes.refreshCalendarData]: {
    next(state: Map<string, any>, action: any) {
      const { payload } = action;
      return state.merge({
        refreshTrigger: payload?.dateRange,
      });
    },
  },

  /** Set when saveAIContentSetting succeeds so next generate uses override=true. */
  [ActionTypes.saveAIContentSetting]: {
    next(state: Map<string, any>, action: any) {
      const { payload, error } = action;
      if (error || !payload) return state;
      return state.merge({ settingsChangedSinceLastGenerate: true });
    },
  },

  [ActionTypes.clearAiSettingsChangedSinceLastGenerate]: {
    next(state: Map<string, any>) {
      return state.merge({ settingsChangedSinceLastGenerate: false });
    },
  },

  [ActionTypes.setCurrentDate]: {
    next(state: Map<string, any>, action: any) {
      const { payload } = action;
      return state.merge({
        currentDate: payload?.currentDate || new Date(),
      });
    },
  },

  [ActionTypes.updateSocialPosts]: {
    next(state, { payload }) {
      if (payload === null) {
        return state;
      }

      const scheduledTime = payload.postDetail.data?.scheduledTime?.value;
      if (scheduledTime) {
        payload.postDetail.data.scheduledTimeUTC = scheduledTime;
      }

      const socialPosts = state.get("socialPosts").toJS();
      const newState = {
        ...state.toJS(),
        socialPosts: socialPosts.map((item) =>
          item.id === payload.postDetail?.id ? payload.postDetail : item
        ),
      };
      return fromJS(newState);
    },
  },

  [ActionTypes.updatePostDetail]: {
    next(state, { payload }) {
      if (payload === null) {
        return state;
      }
      const newState = {
        ...state.toJS(),
        postDetail: payload.postDetail,
      };
      return fromJS(newState);
    },
  },

  [ActionTypes.setSocialPosts]: {
    next(state: Map<string, any>, { payload }) {
      const posts = Array.isArray(payload?.posts) ? payload.posts : [];
      if (posts.length === 0) {
        return state.merge({ socialPosts: fromJS([]) });
      }

      const toUtcIso = (raw: unknown): string | null => {
        if (raw == null) return null;

        const dateObj =
          typeof raw === "number"
            ? new Date(raw)
            : typeof raw === "string"
              ? (() => {
                  try {
                    return parseISO(raw);
                  } catch {
                    return null;
                  }
                })()
              : raw instanceof Date
                ? raw
                : null;

        return dateObj && isValid(dateObj)
          ? formatInTimeZone(dateObj, "UTC", "yyyy-MM-dd'T'HH:mm:ss.SSS'Z'")
          : null;
      };

      const postsWithScheduledTimeUTC = posts.map((post) => {
        const raw = post?.data?.scheduledTime?.value ?? post?.data?.scheduledTime;
        const scheduledTimeUTC = raw ? toUtcIso(raw) : null;

        return scheduledTimeUTC ? { ...post, data: { ...post.data, scheduledTimeUTC } } : post;
      });

      return state.merge({ socialPosts: fromJS(postsWithScheduledTimeUTC) });
    },
  },

  [ActionTypes.showPostDetailDialog]: {
    next(state, { payload, error, pending }) {
      if (error) {
        return state.merge({ error, pending });
      }
      if (pending) {
        return state.merge({ pending });
      }
      if (payload == null || payload === undefined) {
        return state.merge({ pending, error });
      }
      const newState = {
        ...state.toJS(),
        pending: false,
        showPostDetail: true,
        postDetail: payload || null,
      };
      return fromJS(newState);
    },
  },

  [ActionTypes.hidePostDetailDialog]: {
    next(state, {}) {
      const newState = {
        ...state.toJS(),
        showPostDetail: false,
        postDetail: null,
      };
      return fromJS(newState);
    },
  },

  [ActionTypes.updateSharePost]: {
    next(state, { payload }) {
      if (payload === null) {
        return state;
      }
      const newState = {
        ...state.toJS(),
        sharePost: payload.sharePost,
      };
      return fromJS(newState);
    },
  },

  [ActionTypes.showSharePostDialog]: {
    next(state, { payload }) {
      if (payload == null) {
        return state;
      }
      const newState = {
        ...state.toJS(),
        showSharePost: true,
        sharePost: payload.sharePost,
      };
      return fromJS(newState);
    },
  },

  [ActionTypes.hideSharePostDialog]: {
    next(state, {}) {
      const newState = {
        ...state.toJS(),
        showSharePost: false,
        sharePost: null,
      };
      return fromJS(newState);
    },
  },

  [ActionTypes.getCanvaDesigns]: {
    next(state, { payload, error, pending }) {
      if (error) {
        return state.merge({
          pending: false,
          canvaDesigns: [],
        });
      }
      if (pending) {
        return state.merge({ pending });
      }
      if (payload == null || payload === undefined) {
        return state.merge({
          pending: false,
          canvaDesigns: [],
        });
      }
      const designs = payload?.data?.items || payload?.items || [];
      const newState = {
        ...state.toJS(),
        pending: false,
        canvaDesigns: designs,
      };
      return fromJS(newState);
    },
  },

  [ActionTypes.getFileImages]: {
    next(state, { payload, error, pending }) {
      if (error) {
        return state.merge({ error, pending });
      }
      if (pending) {
        return state.merge({ pending });
      }
      if (payload == null || payload === undefined) {
        return state.merge({ pending, error });
      }
      const newState = {
        ...state.toJS(),
        pending: false,
        fileImages: payload?.pageResult?.content || [],
      };
      return fromJS(newState);
    },
  },

  // TopicsModal actions
  [ActionTypes.openTopicsModal]: {
    next(state: any, {}) {
      const newState = {
        ...state.toJS(),
        isTopicsModalOpen: true,
      };
      return fromJS(newState);
    },
  },

  [ActionTypes.closeTopicsModal]: {
    next(state: any, {}) {
      const newState = {
        ...state.toJS(),
        isTopicsModalOpen: false,
        selectedTopicIds: [],
        selectedTopic: null,
      };
      return fromJS(newState);
    },
  },

  [ActionTypes.selectTopic]: {
    next(state: Map<string, any>, { payload }: any) {
      const { topicId } = payload;
      const currentSelectedIds: List<string> = state.get("selectedTopicIds");

      if (!currentSelectedIds.includes(topicId)) {
        const newSelectedIds = currentSelectedIds.push(topicId);
        return state.merge({
          selectedTopicIds: newSelectedIds,
        });
      }

      return state;
    },
  },

  [ActionTypes.deselectTopic]: {
    next(state: Map<string, any>, { payload }: any) {
      const { topicId } = payload;
      const currentSelectedIds: List<string> = state.get("selectedTopicIds");
      const newSelectedIds = currentSelectedIds.filter((id: string) => id !== topicId);

      return state.merge({
        selectedTopicIds: newSelectedIds,
      });
    },
  },

  [ActionTypes.selectAllTopics]: {
    next(state: Map<string, any>, { payload }: any) {
      const { topicIds } = payload;

      return state.merge({
        selectedTopicIds: fromJS(topicIds) as List<string>,
      });
    },
  },

  [ActionTypes.deselectAllTopics]: {
    next(state: Map<string, any>, {}: any) {
      return state.merge({
        selectedTopicIds: List<string>(),
      });
    },
  },

  [ActionTypes.confirmTopicSelection]: {
    next(state: Map<string, any>, { payload }: any) {
      const { selectedTopic } = payload;

      return state.merge({
        selectedTopic,
        isTopicsModalOpen: false,
      });
    },
  },

  [ActionTypes.generateContents]: {
    next(state: Map<string, any>, action: any) {
      const { payload, error } = action;
      if (error) {
        return state.merge({
          error,
        });
      }
      const data = payload?.data;
      const contentList = data?.result?.contentList;
      const generatedContents = contentListToContentByDate(contentList) || {};
      const apiMetadata =
        data != null
          ? {
              token_usage: {
                prompt_tokens: data.promptTokens ?? 0,
                completion_tokens: data.completionTokens ?? 0,
                total_tokens: (data.promptTokens ?? 0) + (data.completionTokens ?? 0),
              },
            }
          : null;
      return state.merge({
        generatedContents,
        apiMetadata,
        error: null,
      });
    },
  },

  [ActionTypes.clearSocialPosts]: {
    next(state: Map<string, any>) {
      return state.merge({
        socialPosts: List(),
        error: null,
      });
    },
  },

  [ActionTypes.setPlatformFilter]: {
    next(state: Map<string, any>, action: any) {
      const { payload } = action;
      const platform = payload?.platform || payload || "All";
      return state.merge({
        platformFilter: String(platform),
      });
    },
  },

  [ActionTypes.setStatusFilter]: {
    next(state: Map<string, any>, action: any) {
      const { payload } = action;
      const status = payload?.status || payload || "All";
      return state.merge({
        statusFilter: String(status),
      });
    },
  },

  [ActionTypes.setTimezoneFilter]: {
    next(state: Map<string, any>, action: any) {
      const { payload } = action;
      const timezone = payload?.timezone || payload || "All";
      return state.merge({
        timezoneFilter: String(timezone),
      });
    },
  },

  [ActionTypes.setTimezone]: {
    next(state: Map<string, any>, action: any) {
      const { payload } = action;
      const timezone = payload?.timezone || payload;
      if (!timezone) return state;
      return state.merge({
        timezone: String(timezone),
      });
    },
  },

  [ActionTypes.resetAIContentState]: {
    next() {
      return initialState;
    },
  },
};

export const aiContentReducer = handleActions(actions, initialState);
